import React, { useState, useEffect } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import {
  determineClimateClassification,
  getDetailedClimateColor,
} from "../utils/climateClassification";
import koppenDescriptions from "../data/koppen-descriptions.json";
import {
  OPENROUTER_API_KEY,
  API_BASE_URL,
  isConfigValid,
} from "../utils/config";
import TemperatureAnalysis from "./TemperatureAnalysis";
import HumidityAnalysis from "./HumidityAnalysis";
import CloudCoverageAnalysis from "./CloudCoverageAnalysis";
import WindAnalysis from "./WindAnalysis";
import SolarRadiationAnalysis from "./SolarRadiationAnalysis";

const MacroClimateView = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [temperatureData, setTemperatureData] = useState(null);
  const [precipitationData, setPrecipitationData] = useState(null);
  const [cloudCoverData, setCloudCoverData] = useState(null);
  const [solarRadiationData, setSolarRadiationData] = useState(null);
  const [humidityData, setHumidityData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [climateInfo, setClimateInfo] = useState(null);
  const [designRecommendations, setDesignRecommendations] = useState(null);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [hoveredAspect, setHoveredAspect] = useState(null);
  const [showThermalPopup, setShowThermalPopup] = useState(false);
  const [showVentilationPopup, setShowVentilationPopup] = useState(false);
  const [showSolarPopup, setShowSolarPopup] = useState(false);
  const [showNavigation, setShowNavigation] = useState(false);
  const [hoveredNav, setHoveredNav] = useState(false);
  const [activeSection, setActiveSection] = useState("");

  // Navigation sections data
  const navigationSections = [
    {
      id: "temperature-analysis-section",
      name: "Temperature",
      color: "bg-red-500",
      hoverColor: "hover:bg-red-500",
    },
    {
      id: "humidity-analysis-section",
      name: "Humidity",
      color: "bg-blue-500",
      hoverColor: "hover:bg-blue-500",
    },
    {
      id: "cloud-coverage-analysis-section",
      name: "Cloud Coverage",
      color: "bg-amber-500",
      hoverColor: "hover:bg-amber-500",
    },
    {
      id: "wind-analysis-section",
      name: "Wind",
      color: "bg-emerald-500",
      hoverColor: "hover:bg-emerald-500",
    },
    {
      id: "solar-radiation-analysis-section",
      name: "Solar Radiation",
      color: "bg-orange-500",
      hoverColor: "hover:bg-orange-500",
    },
  ];

  // Track scroll position to show/hide navigation and detect active section
  useEffect(() => {
    const handleScroll = () => {
      const temperatureSection = document.getElementById(
        "temperature-analysis-section"
      );
      if (temperatureSection) {
        const rect = temperatureSection.getBoundingClientRect();
        // Show navigation when temperature section is at or above viewport
        setShowNavigation(rect.top <= window.innerHeight * 0.8);
      }

      // Detect which section is currently in view
      let currentActiveSection = "";
      const sections = navigationSections.map((section) => section.id);

      for (let i = sections.length - 1; i >= 0; i--) {
        const element = document.getElementById(sections[i]);
        if (element) {
          const rect = element.getBoundingClientRect();
          // Consider a section active if it's in the top 50% of the viewport
          if (rect.top <= window.innerHeight * 0.5) {
            currentActiveSection = sections[i];
            break;
          }
        }
      }

      setActiveSection(currentActiveSection);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Function to scroll to section
  const scrollToSection = (sectionId) => {
    const targetSection = document.getElementById(sectionId);
    if (targetSection) {
      setTimeout(() => {
        targetSection.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    }
  };

  // Thermal strategies for Csb climate
  const thermalStrategies = [
    {
      title: "FORM FOR HEATING",
      description:
        "Buildings with large south facing walls and glazing areas exposed to the south (in the northern hemisphere) can benefit from passive heating in the winter. The goal of passive solar heating systems is to absorb the sun's heat within the building's materials, store it, and release the heat during the nighttime, to maintain a comfortable room temperature.",
      details:
        "Elongate a building along the east-west axis to maximize the surface area exposed to direct winter sunlight. Locate occupied spaces along the solar side of the building. Alternatively, stagger, step, stack, and/or align indoor spaces and building forms to ensure they have adequate wall surface areas facing the equator.",
    },
    {
      title: "THERMAL STORAGE WALL",
      description:
        "A thermal storage wall is a masonry wall placed behind the glazing facing the equator that will absorb and store sunlight (heat) during the daytime and release it back at night for space heating.",
      details:
        "Size the wall and solar glazing area as a percentage of the floor area to be heated. For temperate climates, use 20% at 28º – 40º latitude or 30% at 44º – 56º latitude. Make the exterior wall surface a dark color, and make the thickness of the wall: 30 – 46 cm for concrete, 25 – 36 cm for brick, or 20 – 30 cm for adobe.",
    },
    {
      title: "BUILDING FACADES",
      description:
        "Climate-responsive building façades filter solar radiation, daylight, and fresh air from the exterior as appropriate based on climate/seasonal variations to improve indoor comfort.",
      details:
        "For mixed climates like Csb: In warm season, use passive cooling, shaded glazing, and reflected daylighting. In cold season, use passive solar heating and daylighting. Use insulation (min.): RSI 3.5 (R 20) and double glazing with thermal break.",
    },
    {
      title: "FORM FOR COOLING",
      description:
        "The shape and volume of buildings should be compact and elongated along the east-west axis to reduce heat gain in the summer while maximizing solar exposure in the winter.",
      details:
        "Shape a building so that it is elongated in the east-west direction and has a narrow floor plate for incorporating cross or stack ventilation. Shade east and west walls and all glazed openings during the summer and make the roof a light color.",
    },
  ];

  // Ventilation strategies for Csb climate
  const ventilationStrategies = [
    {
      title: "STACK VENTILATION",
      description:
        "Stack ventilation can be used to increase ventilation where the breeze is not sufficient. When air inside a building is warmer than the outside air, the warmer, less dense air will rise.",
      details:
        "Locate inlet and outlet openings with a minimum of 3 meters (10 feet) apart in height for residential spaces, or 4.6 meters (15 feet) for commercial spaces. The greater the height between openings, the greater the air movement. Locate inlet openings below the height of an occupant's upper body – 0.76 m to 1.37 m above finished floor.",
    },
    {
      title: "CROSS VENTILATION",
      description:
        "Natural ventilation is a passive strategy that uses the natural forces of wind and buoyancy to deliver fresh air into buildings. It regulates the indoor environment and changes the air in the building through openings in the façade and/or roof.",
      details:
        "Locate inlet openings perpendicular to (or at most ±45° from) the prevailing winds. Locate outlet openings on the opposite side of inlet openings, and make them equal to, or greater in size than, the inlet openings. Maintain an unobstructed path between inlet and outlet openings for adequate airflow.",
    },
    {
      title: "NIGHT VENT COOLING",
      description:
        "Night ventilation is a passive cooling strategy that uses outdoor diurnal temperature swings during the summer and the building's thermal mass to cool down the interior space through increased outdoor airflow at night.",
      details:
        "Incorporate thermal mass in the walls, floor and/or ceiling with a minimum of 10.2 cm (4 inches) in thickness, with an exposed surface area of 1 to 3 times the floor area. The more surface area of mass in a space, the more stable the indoor temperature. Use cross or stack ventilation to cool the mass at night.",
    },
  ];

  // Solar strategies for Csb climate
  const solarStrategies = [
    {
      title: "DIRECT GAIN: HEAT STORAGE",
      description:
        "Direct gain is the simplest passive solar heating technique. Sunlight enters the house through south-facing windows and is absorbed by thermal mass materials in the house such as masonry floors and walls.",
      details:
        "Distribute thermal mass throughout the space with a thickness of 10-15 cm (4-6 inches). Use dark-colored, textured surfaces to maximize absorption. Locate 50-70% of the thermal mass within direct sunlight and the remaining mass out of direct sunlight but exposed to reflected or indirect light.",
    },
    {
      title: "SOLAR GREENHOUSE",
      description:
        "A solar greenhouse is a glass room or extension attached to the south side of a building. It collects solar energy which can be used to heat the adjoining spaces, while also providing a space for plants and outdoor living.",
      details:
        "Locate the greenhouse on the equator-facing side of the building (south in the Northern Hemisphere). Size the glazing area to be 0.65 to 1.5 times the floor area of the greenhouse. Include operable vents at the top and bottom of the greenhouse to prevent overheating in summer.",
    },
    {
      title: "DIRECT GAIN: GLAZING",
      description:
        "Solar glazing refers to the windows or other transparent materials that allow sunlight to enter a building for passive solar heating. Properly designed solar glazing maximizes solar heat gain in winter and minimizes it in summer.",
      details:
        "Orient glazing within 30° of true south (in the Northern Hemisphere). Size the glazing area to be 12-20% of the floor area to be heated for temperate climates. Use double or triple glazing with low-e coating to reduce heat loss while allowing solar gain.",
    },
    {
      title: "CLERESTORIES AND SKYLIGHTS",
      description:
        "Clerestories and well-designed skylights are effective strategies for admitting daylight deeper into the building while reducing direct sunlight and glare and maintaining a high level of privacy.",
      details:
        "When designed appropriately they can help increase solar gain during the winter inside the building. If the clerestory windows are adjustable, they have the additional benefit of adding cross ventilation to the space.",
    },
    {
      title: "SHADING DEVICES",
      description:
        "Shading devices, such as overhangs and vertical fins, are necessary to decrease heat gain from solar radiation during the summer. The design of shading devices depends on the solar orientation of a particular building facade.",
      details:
        "Shading devices must be optimized to block solar radiation in the summer while allowing solar heat gain in the winter. External shading devices can reduce solar heat gain through glazing by up to 80%. Use horizontal overhangs for equator-facing facades and vertical fins for east/west facades.",
    },
  ];

  // Popup component for displaying strategies
  const StrategyPopup = ({ strategies, onClose }) => {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-6 max-w-3xl max-h-[80vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-gray-800">
              Design Strategies
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <div className="space-y-6">
            {strategies.map((strategy, index) => (
              <div
                key={index}
                className="border-b border-gray-200 pb-4 last:border-b-0"
              >
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  {strategy.title}
                </h3>
                <p className="text-gray-600 mb-2">{strategy.description}</p>
                <p className="text-gray-700">{strategy.details}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Check for required environment variables on component mount
  useEffect(() => {
    const configStatus = isConfigValid();
    if (!configStatus.valid) {
      console.error("Missing environment variables:", configStatus.missingVars);
      setError(
        `Missing required environment variables: ${configStatus.missingVars.join(
          ", "
        )}. Please check your .env file and restart the application.`
      );
    }
  }, []);

  // Initialize with location from navigation state
  useEffect(() => {
    if (location.state?.selectedLocation) {
      setSelectedLocation(location.state.selectedLocation);
    }
  }, [location.state]);

  // Fetch climate data when a location is selected
  useEffect(() => {
    if (selectedLocation) {
      fetchClimateData(selectedLocation.url, "temperature");
      fetchClimateData(selectedLocation.url, "precipitation");
      fetchClimateData(selectedLocation.url, "cloudcover");
      fetchClimateData(selectedLocation.url, "solarradiation");
      fetchClimateData(selectedLocation.url, "humidity");
    }
  }, [selectedLocation]);

  // Calculate climate info when data is available
  useEffect(() => {
    if (temperatureData?.data && precipitationData?.data) {
      const info = determineClimateClassification(
        temperatureData.data,
        precipitationData.data
      );
      setClimateInfo(info);
    }
  }, [temperatureData, precipitationData]);

  // Fetch climate data for a specific location
  const fetchClimateData = async (url, variable) => {
    setLoading(true);
    setError(null);
    try {
      const apiUrl = `${API_BASE_URL}/api/climate-data?url=${encodeURIComponent(
        url
      )}&variable=${variable}`;

      const response = await fetch(apiUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();

      if (variable === "temperature") {
        setTemperatureData(data);
      } else if (variable === "precipitation") {
        setPrecipitationData(data);
      } else if (variable === "cloudcover") {
        setCloudCoverData(data);
      } else if (variable === "solarradiation") {
        setSolarRadiationData(data);
      } else if (variable === "humidity") {
        setHumidityData(data);
      }
    } catch (err) {
      setError(`Failed to fetch ${variable} data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Calculate cloud coverage statistics
  const calculateCloudCoverStats = () => {
    if (
      !cloudCoverData ||
      !cloudCoverData.data ||
      cloudCoverData.data.length === 0
    ) {
      // Fallback to precipitation-based estimation if no cloud cover data
      if (climateInfo) {
        const precip = parseFloat(climateInfo.annualPrecipitation);
        let coverage;

        if (climateInfo.mainClass === "B") {
          // Arid climates have less cloud coverage
          coverage = Math.min(Math.round(30 + precip * 0.02), 60);
        } else if (climateInfo.mainClass === "A") {
          // Tropical climates have more cloud coverage
          coverage = Math.min(Math.round(60 + precip * 0.01), 85);
        } else {
          // Temperate and continental climates
          coverage = Math.min(Math.round(45 + precip * 0.015), 75);
        }

        return {
          annualMean: coverage,
          solarPotential: 100 - coverage,
          description: getSkyCondition(coverage),
        };
      }
      return null;
    }

    // Calculate actual cloud cover statistics from data
    const dailyData = cloudCoverData.data;
    const annualMean =
      dailyData.reduce((sum, day) => sum + day.mean, 0) / dailyData.length;

    return {
      annualMean: Math.round(annualMean),
      solarPotential: Math.round(100 - annualMean),
      description: getSkyCondition(annualMean),
    };
  };

  // Calculate solar radiation statistics
  const calculateSolarRadiationStats = () => {
    if (
      !solarRadiationData ||
      !solarRadiationData.data ||
      solarRadiationData.data.length === 0
    ) {
      // Fallback to temperature-based estimation if no radiation data
      if (climateInfo) {
        const annualTemp = parseFloat(climateInfo.annualTemp);
        // Use a value close to the backend's calculation of 4761 Wh/m²/day
        const baseRadiation = 4500 + annualTemp * 10;

        return {
          dailyAverage: Math.round(baseRadiation),
          description: getSolarDescription(climateInfo.mainClass),
        };
      }
      return null;
    }

    // Calculate actual solar radiation statistics from data
    const radiationData = solarRadiationData.data;

    // For hourly data, we need to calculate daily totals
    if (radiationData[0] && radiationData[0].hour !== undefined) {
      // Group by day of year
      const dailyTotals = {};

      radiationData.forEach((item) => {
        const dayOfYear = item.day_of_year;

        // Initialize day data if not exists
        if (!dailyTotals[dayOfYear]) {
          dailyTotals[dayOfYear] = {
            totalRadiation: 0,
            hoursWithData: 0,
          };
        }

        // Add radiation for this hour (if it has data)
        if (
          (item.global_horizontal_rad !== undefined &&
            item.global_horizontal_rad > 0) ||
          (item.direct_normal_rad !== undefined &&
            item.diffuse_horizontal_rad !== undefined)
        ) {
          // Use global horizontal radiation if available
          if (item.global_horizontal_rad !== undefined) {
            dailyTotals[dayOfYear].totalRadiation += item.global_horizontal_rad;
          }
          // Otherwise use direct + diffuse
          else if (
            item.direct_normal_rad !== undefined &&
            item.diffuse_horizontal_rad !== undefined
          ) {
            dailyTotals[dayOfYear].totalRadiation +=
              item.direct_normal_rad + item.diffuse_horizontal_rad;
          }

          dailyTotals[dayOfYear].hoursWithData++;
        }
      });

      // Calculate average daily radiation
      let totalDailyRadiation = 0;
      let daysWithData = 0;

      Object.entries(dailyTotals).forEach(([dayOfYear, data]) => {
        if (data.hoursWithData > 0) {
          // For days with partial data, scale up to 24 hours
          const dailyTotal = data.totalRadiation * (24 / data.hoursWithData);
          totalDailyRadiation += dailyTotal;
          daysWithData++;
        }
      });

      // Calculate average daily radiation
      const averageDailyRadiation =
        daysWithData > 0 ? totalDailyRadiation / daysWithData : 0;

      return {
        dailyAverage: Math.round(averageDailyRadiation),
        description: getSolarDescription(climateInfo?.mainClass),
      };
    }
    // For daily data (no hour field)
    else {
      // Calculate average daily radiation
      let totalRadiation = 0;
      let daysCount = 0;

      radiationData.forEach((item) => {
        if (item.mean !== undefined) {
          totalRadiation += item.mean;
          daysCount++;
        } else if (item.global_horizontal_rad !== undefined) {
          totalRadiation += item.global_horizontal_rad;
          daysCount++;
        }
      });

      // Calculate average daily radiation
      const averageDailyRadiation =
        daysCount > 0 ? totalRadiation / daysCount : 0;

      return {
        dailyAverage: Math.round(averageDailyRadiation),
        description: getSolarDescription(climateInfo?.mainClass),
      };
    }
  };

  // Function to determine sky condition based on cloud cover percentage
  const getSkyCondition = (cloudCover) => {
    if (cloudCover <= 30) return "Clear Skies";
    if (cloudCover <= 70) return "Partly Cloudy";
    return "Mostly Cloudy";
  };

  // Function to determine solar radiation description based on climate class
  const getSolarDescription = (mainClass) => {
    if (mainClass === "A") return "High Year-round Radiation";
    if (mainClass === "B") return "Intense Daytime Radiation";
    if (mainClass === "C") return "Moderate Seasonal Radiation";
    if (mainClass === "D") return "Strong Summer Radiation";
    return "Low Radiation Levels";
  };

  // Get detailed description from the Köppen classification JSON
  const getDetailedDescription = () => {
    if (!climateInfo) return "";

    const { mainClass, subClass } = climateInfo;

    // If we don't have a valid classification, return a default message
    if (mainClass === "Unknown" || !koppenDescriptions[mainClass]) {
      return "This location's climate does not fit standard Köppen-Geiger classifications based on the available data.";
    }

    let description = koppenDescriptions[mainClass].description;

    // Add subtype description if available
    if (subClass && koppenDescriptions[mainClass].subtypes) {
      // Extract the precipitation pattern (first letter of subClass)
      const precipPattern = subClass.charAt(0);

      if (koppenDescriptions[mainClass].subtypes[precipPattern]) {
        description +=
          " " +
          koppenDescriptions[mainClass].subtypes[precipPattern].description;

        // Add temperature modifier description if available (for C and D climates)
        if (
          (mainClass === "C" || mainClass === "D") &&
          subClass.length > 1 &&
          koppenDescriptions[mainClass].subtypes[precipPattern]
            .temperature_modifiers
        ) {
          const tempModifier = subClass.charAt(1);
          if (
            koppenDescriptions[mainClass].subtypes[precipPattern]
              .temperature_modifiers[tempModifier]
          ) {
            description +=
              " This region is " +
              koppenDescriptions[mainClass].subtypes[precipPattern]
                .temperature_modifiers[tempModifier];
          }
        }
      }
    }

    return description;
  };

  // Generate comprehensive climate description based on actual data numbers
  const getComprehensiveClimateDescription = () => {
    if (!climateInfo) return "Climate classification data available.";

    const annualTemp = parseFloat(climateInfo.annualTemp);
    const annualPrecip = parseFloat(climateInfo.annualPrecipitation);
    const tempRange = parseFloat(climateInfo.tempRange);
    const cloudCoverStats = calculateCloudCoverStats();
    const humidityStats = calculateHumidityStats();
    const { mainClass, subClass } = climateInfo;

    // Build classification-specific explanation
    let description = "";

    // Explain the main climate class first
    if (mainClass === "A") {
      description += `This location qualifies as a Tropical climate (A) because its coldest month averages above 18°C. `;
      if (subClass === "f") {
        description += `The 'f' designation indicates no dry season, with all months receiving substantial rainfall. `;
      } else if (subClass === "m") {
        description += `The 'm' designation indicates a monsoon pattern with a short dry season offset by heavy rains. `;
      } else if (subClass.includes("w") || subClass.includes("s")) {
        description += `The precipitation pattern shows a distinct ${
          subClass.includes("w") ? "winter" : "summer"
        } dry season. `;
      }
    } else if (mainClass === "B") {
      const aridThreshold = 2 * annualTemp + 280;
      description += `This location is classified as Arid (B) because its annual precipitation of ${Math.round(
        annualPrecip
      )}mm falls below the aridity threshold of ${Math.round(
        aridThreshold
      )}mm for its temperature regime. `;
      if (subClass.includes("W")) {
        description += `The 'W' (desert) classification applies because precipitation is less than half the aridity threshold. `;
      } else if (subClass.includes("S")) {
        description += `The 'S' (steppe) classification applies because precipitation exceeds half but remains below the full aridity threshold. `;
      }
      if (subClass.includes("h")) {
        description += `The 'h' modifier indicates hot conditions with annual temperatures averaging ${annualTemp}°C (above 18°C). `;
      } else if (subClass.includes("k")) {
        description += `The 'k' modifier indicates cool conditions with annual temperatures averaging ${annualTemp}°C (below 18°C). `;
      }
    } else if (mainClass === "C") {
      description += `This location qualifies as Temperate (C) because its coldest month averages between 0-18°C and warmest month exceeds 10°C. `;
      if (subClass.includes("s")) {
        description += `The 's' (Mediterranean) pattern is evident from dry summers and wet winters - the driest summer month receives less than 30mm and less than one-third of the wettest winter month's precipitation. `;
      } else if (subClass.includes("w")) {
        description += `The 'w' (dry winter) pattern shows the driest winter month receives less than 30mm and less than one-tenth of the wettest summer month's precipitation. `;
      } else if (subClass.includes("f")) {
        description += `The 'f' designation indicates no pronounced dry season, with adequate precipitation distributed throughout the year. `;
      }
      if (subClass.includes("a")) {
        description += `The 'a' modifier indicates hot summers with the warmest month averaging above 22°C. `;
      } else if (subClass.includes("b")) {
        description += `The 'b' modifier indicates warm summers with the warmest month below 22°C but at least four months above 10°C. `;
      } else if (subClass.includes("c")) {
        description += `The 'c' modifier indicates cool summers with fewer than four months averaging above 10°C. `;
      }
    } else if (mainClass === "D") {
      description += `This location is classified as Continental (D) because its coldest month averages below 0°C while the warmest month exceeds 10°C, creating significant seasonal temperature contrast. `;
      if (subClass.includes("s")) {
        description += `The 's' pattern indicates dry summers with distinct summer drought conditions. `;
      } else if (subClass.includes("w")) {
        description += `The 'w' pattern shows dry winters typical of continental interiors with monsoon-influenced precipitation. `;
      } else if (subClass.includes("f")) {
        description += `The 'f' designation indicates consistent precipitation throughout the year without a pronounced dry season. `;
      }
      if (subClass.includes("a")) {
        description += `The 'a' modifier indicates hot summers with the warmest month averaging above 22°C. `;
      } else if (subClass.includes("b")) {
        description += `The 'b' modifier indicates warm summers with the warmest month below 22°C but adequate warmth for at least four months. `;
      } else if (subClass.includes("c")) {
        description += `The 'c' modifier indicates cool, short summers with limited growing season. `;
      } else if (subClass.includes("d")) {
        description += `The 'd' modifier indicates extremely cold winters with the coldest month below -38°C. `;
      }
    } else if (mainClass === "E") {
      description += `This location qualifies as Polar (E) because even the warmest month averages below 10°C, preventing tree growth. `;
      if (subClass === "T") {
        description += `The 'T' (tundra) classification applies because at least one month averages above 0°C, allowing limited vegetation. `;
      } else if (subClass === "F") {
        description += `The 'F' (ice cap) classification applies because no month averages above 0°C, creating permanently frozen conditions. `;
      }
    }

    // Add supporting numerical context
    description += `With an annual average temperature of ${annualTemp}°C and ${Math.round(
      annualPrecip
    )}mm of precipitation, `;

    // Add temperature variation context
    if (tempRange > 30) {
      description += `the extreme temperature range of ${tempRange.toFixed(
        1
      )}°C demonstrates the continental nature of this climate with severe seasonal contrasts. `;
    } else if (tempRange > 20) {
      description += `the temperature range of ${tempRange.toFixed(
        1
      )}°C shows significant seasonal variation characteristic of mid-latitude locations. `;
    } else if (tempRange > 15) {
      description += `the moderate temperature range of ${tempRange.toFixed(
        1
      )}°C indicates noticeable but not extreme seasonal differences. `;
    } else {
      description += `the small temperature range of ${tempRange.toFixed(
        1
      )}°C reflects the moderating influence of maritime conditions or low latitude location. `;
    }

    // Add atmospheric conditions context
    if (cloudCoverStats && humidityStats) {
      if (cloudCoverStats.annualMean > 70) {
        description += `High cloud coverage at ${cloudCoverStats.annualMean}% combined with ${humidityStats.annualMean}% humidity creates frequently overcast and moist atmospheric conditions.`;
      } else if (cloudCoverStats.annualMean > 50) {
        description += `Moderate cloud coverage of ${cloudCoverStats.annualMean}% with ${humidityStats.annualMean}% humidity provides a balance of clear and cloudy conditions.`;
      } else {
        description += `Low cloud coverage at ${cloudCoverStats.annualMean}% with ${humidityStats.annualMean}% humidity ensures abundant sunshine and clear atmospheric conditions.`;
      }
    }

    return description;
  };

  // Helper function to format location name from title
  const formatLocationName = (title) => {
    if (!title) return "Unknown Location";

    // Extract the city part (typically after the state code)
    const parts = title.split("_");
    if (parts.length < 2) return title;

    // Get the city part (usually the third part after country and state)
    let cityPart = parts.length > 2 ? parts[2] : parts[1];

    // Handle cases like "Oakland.Intl.AP.724930_TMY"
    if (cityPart.includes(".") || cityPart.includes("-")) {
      // First, handle any numeric suffixes or TMY indicators
      cityPart = cityPart.replace(/\.\d+.*$/, ""); // Remove .724930 and anything after
      cityPart = cityPart.replace(/_TMY.*$/, ""); // Remove _TMY and anything after

      // Handle airport indicators
      cityPart = cityPart.replace(/\.Intl\.AP$/, "");
      cityPart = cityPart.replace(/\.AP$/, "");
      cityPart = cityPart.replace(/\.Airport$/, "");

      // Handle common suffixes
      cityPart = cityPart.replace(/\.Intl$/, " International");
      cityPart = cityPart.replace(/\.Int$/, " International");

      // Handle Central Park and similar location specifiers
      if (cityPart.includes("-")) {
        const locationParts = cityPart.split("-");
        // If it's a specific location within a city
        if (locationParts.length > 1 && !locationParts[1].match(/^\d/)) {
          cityPart =
            locationParts[0] +
            " (" +
            locationParts[1].replace(/\./g, " ") +
            ")";
        } else {
          cityPart = locationParts[0];
        }
      }

      // Replace remaining dots with spaces
      cityPart = cityPart.replace(/\./g, " ");

      // Clean up any double spaces
      cityPart = cityPart.replace(/\s+/g, " ").trim();
    }

    return cityPart;
  };

  // Calculate humidity statistics
  const calculateHumidityStats = () => {
    if (!humidityData || !humidityData.data || humidityData.data.length === 0) {
      // Fallback to climate-based estimation if no humidity data
      if (climateInfo) {
        const precip = parseFloat(climateInfo.annualPrecipitation);

        // Estimate humidity based on climate type and temperature
        let humidity;

        if (climateInfo.mainClass === "A") {
          // Tropical climates have high humidity
          humidity = Math.min(85 + precip / 1000, 95);
        } else if (climateInfo.mainClass === "B") {
          // Arid climates have low humidity
          humidity = Math.max(30 + precip / 500, 20);
        } else if (climateInfo.mainClass === "C") {
          // Temperate climates have moderate humidity
          humidity = 60 + precip / 1500;
        } else if (climateInfo.mainClass === "D") {
          // Continental climates have variable humidity
          humidity = 55 + precip / 1200;
        } else {
          // Default estimate
          humidity = 60;
        }

        return {
          annualMean: Math.round(humidity),
          description: getHumidityDescription(humidity),
        };
      }
      return null;
    }

    // Calculate actual humidity statistics from data
    const dailyData = humidityData.data;
    const annualMean =
      dailyData.reduce((sum, day) => sum + day.mean, 0) / dailyData.length;

    return {
      annualMean: Math.round(annualMean),
      description: getHumidityDescription(annualMean),
    };
  };

  // Function to determine humidity description based on percentage
  const getHumidityDescription = (humidity) => {
    if (humidity >= 80) return "Very Humid";
    if (humidity >= 60) return "Humid";
    if (humidity >= 40) return "Moderate Humidity";
    if (humidity >= 20) return "Dry";
    return "Very Dry";
  };

  // Function to highlight climate aspects in the text
  const highlightClimateAspects = (text) => {
    if (!text) return "";

    const aspectKeywords = {
      temperature: [
        "temperature",
        "thermal",
        "heat",
        "cooling",
        "heating",
        "hot",
        "cold",
        "warm",
      ],
      precipitation: [
        "precipitation",
        "rain",
        "rainfall",
        "moisture",
        "wet",
        "dry",
        "water",
        "drainage",
        "humidity",
        "humid",
        "damp",
        "dry air",
        "relative humidity",
      ],
      cloud: [
        "cloud",
        "cloudy",
        "cloudiness",
        "overcast",
        "sky",
        "skies",
        "shade",
        "shadow",
        "shading",
      ],
      radiation: [
        "solar",
        "radiation",
        "sun",
        "sunlight",
        "daylight",
        "sunshine",
        "solar radiation",
        "solar energy",
      ],
      wind: ["wind", "airflow", "breeze", "ventilation", "air movement"],
    };

    // Map aspects to their corresponding section IDs
    const aspectToSectionMap = {
      temperature: "temperature-analysis-section",
      precipitation: "humidity-analysis-section", // precipitation relates to humidity analysis
      cloud: "cloud-coverage-analysis-section",
      radiation: "solar-radiation-analysis-section",
      wind: "wind-analysis-section",
    };

    // Create a map of all keywords to their aspect type
    const keywordMap = {};
    Object.entries(aspectKeywords).forEach(([aspect, keywords]) => {
      keywords.forEach((keyword) => {
        keywordMap[keyword.toLowerCase()] = aspect;
      });
    });

    // Split the text into words and punctuation
    const parts = text.split(/(\b|\s+)/);

    // Map each part to either plain text or a highlighted span
    return parts.map((part, index) => {
      const lowerPart = part.toLowerCase();
      const aspect = keywordMap[lowerPart];

      if (aspect) {
        const sectionId = aspectToSectionMap[aspect];

        return (
          <span
            key={index}
            className="font-bold text-black hover:bg-gray-300 cursor-pointer transition-colors rounded px-1 -mx-1"
            onMouseEnter={() => setHoveredAspect(aspect)}
            onMouseLeave={() => setHoveredAspect(null)}
            onClick={() => {
              if (sectionId) {
                scrollToSection(sectionId);
              }
            }}
            title={`Click to scroll to ${
              aspect.charAt(0).toUpperCase() + aspect.slice(1)
            } Analysis section`}
          >
            {part}
          </span>
        );
      }

      return part;
    });
  };

  // Get cloud cover and solar radiation stats
  const cloudCoverStats = calculateCloudCoverStats();
  const solarRadiationStats = calculateSolarRadiationStats();
  const humidityStats = calculateHumidityStats();

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header - Fixed at top */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-gray-100 border-b border-gray-200 py-5">
        <div className="flex justify-between items-center px-14">
          <Link
            to="/"
            className="text-2xl font-bold no-underline flex items-center"
          >
            <img
              src="/heading-icon-heavy.png"
              alt="ClimaPro.AI"
              className="h-10"
            />
          </Link>
          <nav className="space-x-8">
            <Link
              to="/contact"
              className="text-sm font-bold text-gray-600 hover:text-gray-900"
            >
              CONTACT
            </Link>
            <Link
              to="/about"
              className="text-sm font-bold text-gray-600 hover:text-gray-900"
            >
              ABOUT
            </Link>
          </nav>
        </div>
      </header>

      {/* Content with top padding to account for fixed header */}
      <div className="pt-20">
        {error && (
          <div className="bg-red-50 text-red-700 px-4 py-3 rounded-md mb-5 mx-14 mt-5">
            {error}
          </div>
        )}

        {!selectedLocation ? (
          <div className="text-center py-12">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              No Location Selected
            </h2>
            <p className="text-gray-600 mb-4">
              Please select a location from the homepage to view macro climate
              data.
            </p>
            <Link
              to="/"
              className="inline-block px-6 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700"
            >
              Return to Homepage
            </Link>
          </div>
        ) : (
          <>
            {loading ? (
              <div className="flex justify-center items-center h-[calc(100vh-81px)]">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-800"></div>
                <span className="ml-3 text-gray-600">
                  Loading climate data...
                </span>
              </div>
            ) : (
              <>
                {climateInfo && (
                  <div className="px-14 py-14 pb-4 w-full">
                    {/* Köppen Classification Header */}
                    <div className="mb-8">
                      <p className="text-gray-500 text-sm font-bold mb-1">
                        Köppen-Geiger Climate Classification
                      </p>

                      <div className="flex justify-between items-start">
                        <div className="max-w-4xl">
                          <h1 className="text-4xl font-bold text-gray-900 mb-6 leading-tight">
                            {climateInfo.fullName
                              ? climateInfo.fullName
                                  .split(" ")
                                  .map(
                                    (word) =>
                                      word.charAt(0).toUpperCase() +
                                      word.slice(1)
                                  )
                                  .join(" ")
                              : "Climate Classification Not Available"}
                          </h1>

                          {loadingRecommendations ? (
                            <div className="text-gray-500 leading-relaxed mb-8 font-semibold w-full">
                              <style jsx>{`
                                @keyframes macroWave1 {
                                  0%,
                                  100% {
                                    background-color: #d1d5db;
                                  }
                                  50% {
                                    background-color: #9ca3af;
                                  }
                                }
                                @keyframes macroWave2 {
                                  0%,
                                  100% {
                                    background-color: #9ca3af;
                                  }
                                  50% {
                                    background-color: #d1d5db;
                                  }
                                }
                                @keyframes macroWave3 {
                                  0%,
                                  100% {
                                    background-color: #e5e7eb;
                                  }
                                  50% {
                                    background-color: #9ca3af;
                                  }
                                }
                                .animate-macro-wave-1 {
                                  animation: macroWave1 2s ease-in-out infinite;
                                }
                                .animate-macro-wave-2 {
                                  animation: macroWave2 2s ease-in-out infinite
                                    0.3s;
                                }
                                .animate-macro-wave-3 {
                                  animation: macroWave3 2s ease-in-out infinite
                                    0.6s;
                                }
                              `}</style>
                              <div className="space-y-3 w-full">
                                <div className="h-3 rounded animate-macro-wave-1"></div>
                                <div className="h-3 rounded animate-macro-wave-2 w-5/6"></div>
                                <div className="h-3 rounded animate-macro-wave-3 w-4/5"></div>
                                <div className="h-3 rounded animate-macro-wave-1 w-full"></div>
                                <div className="h-3 rounded animate-macro-wave-2 w-3/4"></div>
                                <div className="h-3 rounded animate-macro-wave-3 w-5/6"></div>
                                <div className="h-3 rounded animate-macro-wave-1 w-4/5"></div>
                                <div className="h-3 rounded animate-macro-wave-2 w-full"></div>
                                <div className="h-3 rounded animate-macro-wave-3 w-2/3"></div>
                              </div>
                            </div>
                          ) : designRecommendations ? (
                            <p className="text-gray-500 leading-relaxed mb-8 font-semibold w-full">
                              {highlightClimateAspects(designRecommendations)}
                            </p>
                          ) : (
                            <p className="text-gray-500 leading-relaxed mb-8 font-semibold">
                              {getComprehensiveClimateDescription()}
                            </p>
                          )}

                          {/* Design Suggestion Text and AI Button */}
                          <div className="flex items-center gap-3 mt-6">
                            <span className="text-gray-500 text-base font-bold">
                              Design Suggestion
                            </span>
                            <button
                              className="bg-gray-600 hover:bg-[#206ab2] text-white px-1.5 py-1 rounded text-xs font-bold transition-colors"
                              onClick={async () => {
                                // Create climate information object
                                const climateData = {
                                  location:
                                    selectedLocation?.city ||
                                    formatLocationName(
                                      selectedLocation?.title
                                    ) ||
                                    "Unknown Location",
                                  coordinates: selectedLocation?.coordinates,
                                  classification: {
                                    mainClass: climateInfo.mainClass,
                                    subClass: climateInfo.subClass,
                                    fullName: climateInfo.fullName,
                                  },
                                  temperature: {
                                    annual: climateInfo.annualTemp,
                                    range: climateInfo.tempRange,
                                    description:
                                      parseFloat(climateInfo.annualTemp) > 22
                                        ? "Hot Climate"
                                        : parseFloat(climateInfo.annualTemp) >
                                          10
                                        ? "Mild Climate"
                                        : "Cold Climate",
                                  },
                                  precipitation: {
                                    annual: parseFloat(
                                      climateInfo.annualPrecipitation
                                    ),
                                    description:
                                      parseFloat(
                                        climateInfo.annualPrecipitation
                                      ) > 1000
                                        ? "High Precipitation"
                                        : parseFloat(
                                            climateInfo.annualPrecipitation
                                          ) > 500
                                        ? "Moderate Precipitation"
                                        : "Low Precipitation",
                                  },
                                  cloudCover: cloudCoverStats,
                                  solarRadiation: solarRadiationStats,
                                  humidity: humidityStats,
                                  detailedDescription: climateInfo.fullName
                                    ? `This location has a ${climateInfo.fullName} climate classification.`
                                    : "Climate classification data available.",
                                };

                                // Log climate information to console
                                console.log(
                                  "Climate Information for AI Agent:",
                                  climateData
                                );

                                // Debug: Check if API key is accessible
                                const configStatus = isConfigValid();
                                console.log(
                                  "Config valid:",
                                  configStatus.valid
                                );
                                console.log("Environment variables:", {
                                  directAccess: process.env
                                    .REACT_APP_OPENROUTER_API_KEY
                                    ? "Available"
                                    : "Not available",
                                  fromConfig: OPENROUTER_API_KEY
                                    ? "Available"
                                    : "Not available",
                                  envKeys: Object.keys(process.env).filter(
                                    (key) => key.startsWith("REACT_APP_")
                                  ),
                                });

                                if (!configStatus.valid) {
                                  console.error(
                                    "Missing environment variables:",
                                    configStatus.missingVars
                                  );
                                  setError(
                                    "API key not configured. Please add your OpenRouter API key to the .env file and restart the development server."
                                  );
                                  return; // Don't proceed with the API call
                                }
                                console.log(
                                  "API Key available:",
                                  OPENROUTER_API_KEY ? "Yes" : "No"
                                );

                                // Make API call to OpenRouter
                                setError(null); // Clear any previous errors
                                setLoadingRecommendations(true);
                                console.log(
                                  "Attempting to call OpenRouter API..."
                                );

                                fetch(
                                  "https://openrouter.ai/api/v1/chat/completions",
                                  {
                                    method: "POST",
                                    headers: {
                                      Authorization: `Bearer ${OPENROUTER_API_KEY}`,
                                      "HTTP-Referer": window.location.origin,
                                      "X-Title": "ClimaPro.AI",
                                      "Content-Type": "application/json",
                                    },
                                    body: JSON.stringify({
                                      model: "anthropic/claude-3.7-sonnet", // Using a more reliable model as default
                                      route: "fallback", // Use fallback routing for better reliability
                                      messages: [
                                        {
                                          role: "system",
                                          content:
                                            "You are an expert architectural consultant specializing in climate-responsive design. Prioritize the location's specific climate characteristics over raw numbers, as local factors can significantly affect how climate is experienced (e.g., San Francisco has high humidity percentages but often feels dry due to ocean breezes and other factors). First, provide a brief summary of the location's climate based on the Köppen-Geiger Climate Classification, highlighting its key characteristics and how it is actually experienced by people living there. Then, identify the priority order of the five key climate aspects (temperature, precipitation, cloud coverage/solar radiation, wind, and humidity) for the given location based on its unique context. Finally, provide specific design suggestions for each aspect, focusing on practical architectural strategies that respond to how the climate is actually experienced in this location(use the climate data to make design suggestions). Format your response as a cohesive paragraph (150-200 words) that flows naturally. Use professional language but keep it accessible. Limit your response to 150-200 words.",
                                        },
                                        {
                                          role: "user",
                                          content: `Based on the climate data for ${
                                            climateData.location
                                          } (Köppen-Geiger: ${
                                            climateData.classification.mainClass
                                          }${
                                            climateData.classification.subClass
                                          }, ${
                                            climateData.temperature.annual
                                          }°C annual temperature, ${
                                            climateData.precipitation.annual
                                          } mm precipitation, ${
                                            climateData.cloudCover
                                              ? climateData.cloudCover
                                                  .annualMean
                                              : "Unknown"
                                          }% cloud coverage, ${
                                            climateData.solarRadiation
                                              ? climateData.solarRadiation
                                                  .dailyAverage
                                              : "Unknown"
                                          } Wh/m² daily radiation, ${
                                            climateData.humidity
                                              ? climateData.humidity.annualMean
                                              : "Unknown"
                                          }% humidity), identify the priority order of the five climate aspects (temperature, precipitation, cloud coverage/solar radiation, wind, and humidity) for this specific location. Consider how the local geography, urban context, and other factors might affect how the climate is actually experienced by people in ${
                                            climateData.location
                                          }, rather than just relying on the raw numbers. Then provide specific design suggestions for each aspect, explaining why certain aspects should be prioritized over others based on this location's unique climate characteristics.`,
                                        },
                                      ],
                                    }),
                                  }
                                )
                                  .then((response) => {
                                    if (!response.ok) {
                                      // Check for specific error status codes
                                      if (response.status === 503) {
                                        throw new Error(
                                          "OpenRouter service is temporarily unavailable. Please try again later."
                                        );
                                      } else if (response.status === 401) {
                                        throw new Error(
                                          "Authentication failed. Please check your API key."
                                        );
                                      } else if (response.status === 429) {
                                        throw new Error(
                                          "Rate limit exceeded. Please try again later."
                                        );
                                      }
                                      throw new Error(
                                        `API request failed with status: ${response.status}`
                                      );
                                    }
                                    return response.json();
                                  })
                                  .then((data) => {
                                    console.log(
                                      "Architecture Design Suggestions:",
                                      data
                                    );
                                    if (
                                      data.choices &&
                                      data.choices[0] &&
                                      data.choices[0].message
                                    ) {
                                      console.log(
                                        "Design Recommendations:",
                                        data.choices[0].message.content
                                      );
                                      setDesignRecommendations(
                                        data.choices[0].message.content
                                      );
                                    } else if (data.error) {
                                      console.error(
                                        "API returned an error:",
                                        data.error
                                      );
                                      throw new Error(
                                        data.error.message ||
                                          "Unknown error from OpenRouter API"
                                      );
                                    }
                                  })
                                  .catch((error) => {
                                    console.error(
                                      "Error fetching design suggestions:",
                                      error
                                    );
                                    setError(
                                      `Failed to get design suggestions: ${error.message}`
                                    );
                                  })
                                  .finally(() => {
                                    setLoadingRecommendations(false);
                                  });
                              }}
                              disabled={loadingRecommendations}
                            >
                              {loadingRecommendations ? "..." : "ClimaPro.AI"}
                            </button>
                          </div>

                          {selectedLocation && (
                            <p className="text-2xl font-bold text-gray-900 mt-6">
                              {(
                                selectedLocation.city ||
                                (selectedLocation.title &&
                                  formatLocationName(selectedLocation.title)) ||
                                "Unknown Location"
                              )
                                .split(" ")
                                .map(
                                  (word) =>
                                    word.charAt(0).toUpperCase() + word.slice(1)
                                )
                                .join(" ")}
                            </p>
                          )}
                        </div>

                        <div className="ml-8">
                          <div
                            className="text-[12rem] font-black leading-none"
                            style={{
                              color: getDetailedClimateColor(
                                climateInfo.mainClass,
                                climateInfo.subClass
                              ),
                            }}
                          >
                            {climateInfo.mainClass}
                            {climateInfo.subClass}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Climate Metrics */}
                    <div className="pt-8 pb-16">
                      <div className="grid grid-cols-5 gap-4">
                        {/* Temperature */}
                        <div
                          className={`pt-4 group hover:cursor-pointer transition-all duration-200 ${
                            hoveredAspect === "temperature" ? "" : ""
                          }`}
                          onClick={() => {
                            // Scroll to TemperatureAnalysis section with improved targeting
                            const targetSection = document.getElementById(
                              "temperature-analysis-section"
                            );
                            if (targetSection) {
                              // Add a longer delay to account for AI content changes
                              setTimeout(() => {
                                targetSection.scrollIntoView({
                                  behavior: "smooth",
                                  block: "start",
                                });
                              }, 200);
                            } else {
                              // Fallback: scroll down more conservatively
                              setTimeout(() => {
                                window.scrollBy({
                                  top: window.innerHeight * 0.8,
                                  behavior: "smooth",
                                });
                              }, 200);
                            }
                          }}
                          onMouseEnter={() => setHoveredAspect("temperature")}
                          onMouseLeave={() => setHoveredAspect(null)}
                        >
                          <p
                            className={`text-sm ${
                              hoveredAspect === "temperature"
                                ? "text-black"
                                : "text-gray-500"
                            } font-bold mb-1 group-hover:text-black`}
                          >
                            Annual Average
                          </p>
                          <div
                            className={`text-4xl font-black ${
                              hoveredAspect === "temperature"
                                ? "text-black"
                                : "text-gray-900"
                            } mb-3 group-hover:text-black`}
                          >
                            {climateInfo.annualTemp}°C
                          </div>
                          <div
                            className={`border-t-2 ${
                              hoveredAspect === "temperature"
                                ? "border-black"
                                : "border-gray-300"
                            } pt-3 mb-0 group-hover:border-black`}
                          >
                            <p
                              className={`font-bold ${
                                hoveredAspect === "temperature"
                                  ? "text-black"
                                  : "text-gray-500"
                              } group-hover:text-black`}
                            >
                              Temperature
                            </p>
                            <p
                              className={`font-bold mb-1 mt-2 ${
                                hoveredAspect === "temperature"
                                  ? "text-black"
                                  : "text-gray-800"
                              } group-hover:text-black`}
                            >
                              {parseFloat(climateInfo.annualTemp) > 22
                                ? "Hot Climate"
                                : parseFloat(climateInfo.annualTemp) > 10
                                ? "Mild Climate"
                                : "Cold Climate"}
                            </p>
                          </div>
                        </div>

                        {/* Precipitation */}
                        <div
                          className={`pt-4 group hover:cursor-pointer transition-all duration-200 ${
                            hoveredAspect === "precipitation" ? "" : ""
                          }`}
                          onClick={() => {
                            // Scroll to HumidityAnalysis section
                            const targetSection = document.getElementById(
                              "humidity-analysis-section"
                            );
                            if (targetSection) {
                              setTimeout(() => {
                                targetSection.scrollIntoView({
                                  behavior: "smooth",
                                  block: "start",
                                });
                              }, 100);
                            }
                          }}
                          onMouseEnter={() => setHoveredAspect("precipitation")}
                          onMouseLeave={() => setHoveredAspect(null)}
                        >
                          <p
                            className={`text-sm ${
                              hoveredAspect === "precipitation"
                                ? "text-black"
                                : "text-gray-500"
                            } font-bold mb-1 group-hover:text-black`}
                          >
                            Annual Total
                          </p>
                          <div
                            className={`text-4xl font-black ${
                              hoveredAspect === "precipitation"
                                ? "text-black"
                                : "text-gray-900"
                            } mb-3 group-hover:text-black`}
                          >
                            {Math.round(
                              parseFloat(climateInfo.annualPrecipitation)
                            )}{" "}
                            mm
                          </div>
                          <div
                            className={`border-t-2 ${
                              hoveredAspect === "precipitation"
                                ? "border-black"
                                : "border-gray-300"
                            } pt-3 mb-0 group-hover:border-black`}
                          >
                            <p
                              className={`font-bold ${
                                hoveredAspect === "precipitation"
                                  ? "text-black"
                                  : "text-gray-500"
                              } group-hover:text-black`}
                            >
                              Precipitation
                            </p>
                            <p
                              className={`font-bold mb-1 mt-2 ${
                                hoveredAspect === "precipitation"
                                  ? "text-black"
                                  : "text-gray-800"
                              } group-hover:text-black`}
                            >
                              {parseFloat(climateInfo.annualPrecipitation) >
                              2000
                                ? "Extremely High Precipitation"
                                : parseFloat(climateInfo.annualPrecipitation) >
                                  1500
                                ? "Very High Precipitation"
                                : parseFloat(climateInfo.annualPrecipitation) >
                                  1000
                                ? "High Precipitation"
                                : parseFloat(climateInfo.annualPrecipitation) >
                                  750
                                ? "Moderate-High Precipitation"
                                : parseFloat(climateInfo.annualPrecipitation) >
                                  500
                                ? "Moderate Precipitation"
                                : parseFloat(climateInfo.annualPrecipitation) >
                                  250
                                ? "Low-Moderate Precipitation"
                                : "Low Precipitation"}
                            </p>
                          </div>
                        </div>

                        {/* Cloud Coverage */}
                        <div
                          className={`pt-4 group hover:cursor-pointer transition-all duration-200 ${
                            hoveredAspect === "cloud" ? "" : ""
                          }`}
                          onClick={() => {
                            // Scroll to CloudCoverageAnalysis section
                            const targetSection = document.getElementById(
                              "cloud-coverage-analysis-section"
                            );
                            if (targetSection) {
                              setTimeout(() => {
                                targetSection.scrollIntoView({
                                  behavior: "smooth",
                                  block: "start",
                                });
                              }, 100);
                            }
                          }}
                          onMouseEnter={() => setHoveredAspect("cloud")}
                          onMouseLeave={() => setHoveredAspect(null)}
                        >
                          <p
                            className={`text-sm ${
                              hoveredAspect === "cloud"
                                ? "text-black"
                                : "text-gray-500"
                            } font-bold mb-1 group-hover:text-black`}
                          >
                            Cloud Coverage
                          </p>
                          <div
                            className={`text-4xl font-black ${
                              hoveredAspect === "cloud"
                                ? "text-black"
                                : "text-gray-900"
                            } mb-3 group-hover:text-black`}
                          >
                            {cloudCoverStats ? cloudCoverStats.annualMean : "—"}
                            %
                          </div>
                          <div
                            className={`border-t-2 ${
                              hoveredAspect === "cloud"
                                ? "border-black"
                                : "border-gray-300"
                            } pt-3 mb-0 group-hover:border-black`}
                          >
                            <p
                              className={`font-bold ${
                                hoveredAspect === "cloud"
                                  ? "text-black"
                                  : "text-gray-500"
                              } group-hover:text-black`}
                            >
                              Solar
                            </p>
                            <p
                              className={`font-bold mb-1 mt-2 ${
                                hoveredAspect === "cloud"
                                  ? "text-black"
                                  : "text-gray-800"
                              } group-hover:text-black`}
                            >
                              {cloudCoverStats
                                ? cloudCoverStats.description
                                : "Unknown"}
                            </p>
                          </div>
                        </div>

                        {/* Wind */}
                        <div
                          className={`pt-4 group hover:cursor-pointer transition-all duration-200 ${
                            hoveredAspect === "wind" ? "" : ""
                          }`}
                          onClick={() => {
                            // Scroll to WindAnalysis section
                            const targetSection = document.getElementById(
                              "wind-analysis-section"
                            );
                            if (targetSection) {
                              setTimeout(() => {
                                targetSection.scrollIntoView({
                                  behavior: "smooth",
                                  block: "start",
                                });
                              }, 100);
                            }
                          }}
                          onMouseEnter={() => setHoveredAspect("wind")}
                          onMouseLeave={() => setHoveredAspect(null)}
                        >
                          <p
                            className={`text-sm ${
                              hoveredAspect === "wind"
                                ? "text-black"
                                : "text-gray-500"
                            } font-bold mb-1 group-hover:text-black`}
                          >
                            Annual Average
                          </p>
                          <div
                            className={`text-4xl font-black ${
                              hoveredAspect === "wind"
                                ? "text-black"
                                : "text-gray-900"
                            } mb-3 group-hover:text-black`}
                          >
                            {(
                              3 +
                              parseFloat(climateInfo.tempRange) / 5
                            ).toFixed(1)}
                            m/s
                          </div>
                          <div
                            className={`border-t-2 ${
                              hoveredAspect === "wind"
                                ? "border-black"
                                : "border-gray-300"
                            } pt-3 mb-0 group-hover:border-black`}
                          >
                            <p
                              className={`font-bold ${
                                hoveredAspect === "wind"
                                  ? "text-black"
                                  : "text-gray-500"
                              } group-hover:text-black`}
                            >
                              Wind
                            </p>
                            <p
                              className={`font-bold mb-1 mt-2 ${
                                hoveredAspect === "wind"
                                  ? "text-black"
                                  : "text-gray-800"
                              } group-hover:text-black`}
                            >
                              {parseFloat(climateInfo.tempRange) > 20
                                ? "Strong Seasonal Winds"
                                : parseFloat(climateInfo.tempRange) > 10
                                ? "Moderate Wind Patterns"
                                : "Consistent Mild Winds"}
                            </p>
                          </div>
                        </div>

                        {/* Radiation */}
                        <div
                          className={`pt-4 group hover:cursor-pointer transition-all duration-200 ${
                            hoveredAspect === "radiation" ? "" : ""
                          }`}
                          onClick={() => {
                            // Scroll to SolarRadiationAnalysis section
                            const targetSection = document.getElementById(
                              "solar-radiation-analysis-section"
                            );
                            if (targetSection) {
                              setTimeout(() => {
                                targetSection.scrollIntoView({
                                  behavior: "smooth",
                                  block: "start",
                                });
                              }, 100);
                            }
                          }}
                          onMouseEnter={() => setHoveredAspect("radiation")}
                          onMouseLeave={() => setHoveredAspect(null)}
                        >
                          <p
                            className={`text-sm ${
                              hoveredAspect === "radiation"
                                ? "text-black"
                                : "text-gray-500"
                            } font-bold mb-1 group-hover:text-black`}
                          >
                            Daily Average
                          </p>
                          <div
                            className={`text-4xl font-black ${
                              hoveredAspect === "radiation"
                                ? "text-black"
                                : "text-gray-900"
                            } mb-3 group-hover:text-black`}
                          >
                            {solarRadiationStats
                              ? solarRadiationStats.dailyAverage
                              : Math.round(
                                  4500 + parseFloat(climateInfo.annualTemp) * 10
                                )}{" "}
                            Wh/m²
                          </div>
                          <div
                            className={`border-t-2 ${
                              hoveredAspect === "radiation"
                                ? "border-black"
                                : "border-gray-300"
                            } pt-3 mb-0 group-hover:border-black`}
                          >
                            <p
                              className={`font-bold ${
                                hoveredAspect === "radiation"
                                  ? "text-black"
                                  : "text-gray-500"
                              } group-hover:text-black`}
                            >
                              Radiation
                            </p>
                            <p
                              className={`font-bold mb-1 mt-2 ${
                                hoveredAspect === "radiation"
                                  ? "text-black"
                                  : "text-gray-800"
                              } group-hover:text-black`}
                            >
                              {solarRadiationStats
                                ? solarRadiationStats.description
                                : getSolarDescription(climateInfo.mainClass)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Temperature Analysis Section */}
                <TemperatureAnalysis
                  climateInfo={climateInfo}
                  temperatureData={temperatureData}
                />

                {/* Humidity Analysis Section */}
                <HumidityAnalysis
                  climateInfo={climateInfo}
                  humidityData={humidityData}
                />

                {/* Cloud Coverage Analysis Section */}
                <CloudCoverageAnalysis
                  climateInfo={climateInfo}
                  cloudCoverData={cloudCoverData}
                  solarRadiationData={solarRadiationData}
                />

                {/* Wind Analysis Section */}
                <WindAnalysis
                  climateInfo={climateInfo}
                  temperatureData={temperatureData}
                />

                {/* Solar Radiation Analysis Section */}
                <SolarRadiationAnalysis
                  climateInfo={climateInfo}
                  solarRadiationData={solarRadiationData}
                  cloudCoverData={cloudCoverData}
                  temperatureData={temperatureData}
                />
              </>
            )}
          </>
        )}
      </div>

      {/* Thermal Strategies Popup */}
      {showThermalPopup && (
        <StrategyPopup
          strategies={thermalStrategies}
          onClose={() => setShowThermalPopup(false)}
        />
      )}

      {/* Ventilation Strategies Popup */}
      {showVentilationPopup && (
        <StrategyPopup
          strategies={ventilationStrategies}
          onClose={() => setShowVentilationPopup(false)}
        />
      )}

      {/* Solar Strategies Popup */}
      {showSolarPopup && (
        <StrategyPopup
          strategies={solarStrategies}
          onClose={() => setShowSolarPopup(false)}
        />
      )}

      {/* Floating Navigation Bar */}
      {showNavigation && (
        <div className="fixed right-6 top-1/2 transform -translate-y-1/2 z-40 w-8">
          <div className="bg-gray-100 rounded-full p-2 transition-all duration-300">
            {navigationSections.map((section, index) => (
              <div
                key={section.id}
                className="flex items-center justify-center mb-2 last:mb-0 cursor-pointer"
                onClick={() => scrollToSection(section.id)}
                title={section.name}
              >
                <div
                  className={`w-3 h-3 border border-black rounded-full transition-all duration-200 ${
                    activeSection === section.id
                      ? section.color
                      : `bg-transparent ${section.hoverColor}`
                  }`}
                ></div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MacroClimateView;
